// Motor API Service - handles token retrieval and vehicle search
import axios from 'axios';

const MOTOR_API_DOMAIN = process.env.REACT_APP_MOTOR_API_DOMAIN || 'https://motor-uat-motor-api.dchmsc.com';
const MOTOR_API_USERNAME = process.env.REACT_APP_MOTOR_API_USERNAME;
const MOTOR_API_PASSWORD = process.env.REACT_APP_MOTOR_API_PASSWORD;

// Cache for token to avoid repeated requests
let cachedToken = null;
let tokenExpiry = null;

/**
 * Get authentication token from Motor API
 * Token is cached for 2 minutes to reduce API calls
 */
export const getMotorApiToken = async () => {
  // Check if we have a valid cached token (cache for 2 minutes)
  const now = Date.now();
  if (cachedToken && tokenExpiry && now < tokenExpiry) {
    return cachedToken;
  }

  try {
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/get-token`,
      new URLSearchParams({
        username: MOTOR_API_USERNAME,
        password: MOTOR_API_PASSWORD,
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    cachedToken = response.data;
    tokenExpiry = now + 2 * 60 * 1000; // Cache for 2 minutes
    
    console.log('Motor API token retrieved successfully');
    return cachedToken;
  } catch (error) {
    console.error('Failed to get Motor API token:', error);
    throw new Error('Failed to authenticate with Motor API');
  }
};

/**
 * Search vehicles by License No. or Contract No.
 * @param {string} searchInput - License No. or Contract No.
 * @returns {Array} - Array of vehicle objects with isSubmitted flag
 */
export const searchVehicles = async (searchInput) => {
  try {
    // Get token first
    const token = await getMotorApiToken();

    // Call vehicle search API
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/mssql-api/escorecard-vehicle-list`,
      {
        token: token,
        LicenseNo: searchInput,
        ContractNo: searchInput,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Process response and add isSubmitted flag
    const vehicles = response.data || [];
    
    return vehicles.map(vehicle => ({
      ...vehicle,
      // isSubmitted = 1 if eScoreCardNo exists and has length > 0
      isSubmitted: vehicle.eScoreCardNo && vehicle.eScoreCardNo.length > 0 ? 1 : 0,
    }));
  } catch (error) {
    console.error('Vehicle search failed:', error);
    throw new Error('Failed to search vehicles');
  }
};

export const motorApiService = {
  getToken: getMotorApiToken,
  searchVehicles,
};

/**
 * Get form items for damage assessment (for Not Submitted records)
 * @returns {Object} - Object with formItems grouped by category and categories array
 */
export const getFormItems = async () => {
  try {
    // Get token first
    const token = await getMotorApiToken();

    // Call post-api to get form items
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/post-api`,
      {
        token: token,
        app_id: 45,
        data: [{
          target_api_code: "esc-1",
          page: 1,
          page_size: 0,
          sort_column: "DisplayOrder",
          sort_order: "asc",
          filter: {
            AND: [
              { "m.IsActive": { equals: 1 } },
              { "r.IsActive": { equals: 1 } }
            ]
          }
        }]
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Extract data from nested response structure
    const resultData = response.data?.result?.[0]?.[0]?.Data || [];
    
    // Group form items by category
    const formItems = {};
    const categoriesMap = {};
    
    resultData.forEach(item => {
      const categoryName = item.CategoryName;
      
      // Build categories map
      if (!categoriesMap[item.CategoryId]) {
        categoriesMap[item.CategoryId] = {
          Id: item.CategoryId,
          Name: categoryName,
          DisplayOrder: item.DisplayOrder
        };
      }
      
      // Group form items by category name
      if (!formItems[categoryName]) {
        formItems[categoryName] = [];
      }
      
      formItems[categoryName].push({
        Id: item.FormItemId,
        CategoryId: item.CategoryId,
        Category: categoryName,
        DamageCode: item.DamageCode,
        Description: item.Description,
        Weight: item.Weight
      });
    });
    
    // Convert categories map to sorted array
    const categories = Object.values(categoriesMap).sort((a, b) => a.DisplayOrder - b.DisplayOrder);
    
    console.log('Form items loaded successfully:', { formItems, categories });
    
    return { formItems, categories };
  } catch (error) {
    console.error('Failed to get form items:', error);
    throw new Error('Failed to load form items');
  }
};

// Update motorApiService to include getFormItems
motorApiService.getFormItems = getFormItems;

/**
 * Generate filename for annotation image based on VehicleID
 * @param {number|string} vehicleId - Vehicle ID from search result
 * @returns {string} - Formatted filename (e.g., '18052.png')
 */
export const generateAnnotationFilename = (vehicleId) => {
  return `${vehicleId}.png`;
};

/**
 * Get S3 presigned URL for uploading files (image or PDF)
 * @param {string} contractNo - Contract number
 * @param {string} fileType - File type: 'image' or 'pdf'
 * @returns {Object} - Presigned URL response with url, key, fileType, bucketName
 */
export const getS3PresignedUrl = async (identifier, fileType = 'image') => {
  try {
    const token = await getMotorApiToken();
    
    let filename, mimeType;
    if (fileType === 'pdf') {
      filename = generatePdfFilename(identifier);
      mimeType = 'application/pdf';
    } else {
      filename = generateAnnotationFilename(identifier);
      mimeType = 'image/png';
    }
    
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/get-s3-presigned-url`,
      {
        token: token,
        app_id: 45,
        bucket_name: "dch-motor-ucc-uat",
        key: `ucc-escore/${filename}`,
        file_type: mimeType
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    console.log(`S3 presigned URL retrieved successfully for ${fileType}`);
    return response.data.result;
  } catch (error) {
    console.error('Failed to get S3 presigned URL:', error);
    throw new Error('Failed to get upload URL');
  }
};

/**
 * Get S3 presigned URL for downloading/reading files
 * @param {string} contractNo - Contract number
 * @param {string} fileType - File type: 'image' or 'pdf'
 * @returns {Object} - Presigned URL response for GET operation
 */
export const getS3PresignedUrlForRead = async (identifier, fileType = 'image') => {
  try {
    const token = await getMotorApiToken();
    
    let filename, mimeType;
    if (fileType === 'pdf') {
      filename = generatePdfFilename(identifier);
      mimeType = 'application/pdf';
    } else {
      filename = generateAnnotationFilename(identifier);
      mimeType = 'image/png';
    }
    
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/get-s3-presigned-url`,
      {
        token: token,
        app_id: 45,
        bucket_name: "dch-motor-ucc-uat",
        key: `ucc-escore/${filename}`,
        file_type: mimeType,
        operation: "getObject" // Request GET presigned URL
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    console.log(`S3 presigned URL for read retrieved successfully for ${fileType}`);
    return response.data.result;
  } catch (error) {
    console.error('Failed to get S3 presigned URL for read:', error);
    throw new Error('Failed to get download URL');
  }
};

/**
 * Fetch PDF from S3 as Blob
 * @param {string} contractNo - Contract number
 * @returns {Promise<Blob|null>} - PDF blob or null if not found
 */
export const getPdfFromS3 = async (contractNo) => {
  try {
    console.log('Fetching PDF from S3 for:', contractNo);
    
    // Get presigned URL for reading PDF
    const presignedResult = await getS3PresignedUrlForRead(contractNo, 'pdf');
    
    if (!presignedResult || !presignedResult.url) {
      console.log('No presigned URL returned for PDF');
      return null;
    }
    
    console.log('Fetching PDF from S3 URL...');
    
    // Fetch the PDF
    const response = await fetch(presignedResult.url, {
      method: 'GET',
    });
    
    if (!response.ok) {
      console.log('PDF not found in S3, status:', response.status);
      throw new Error(`PDF not found in S3 (status: ${response.status})`);
    }
    
    // Return as blob
    const blob = await response.blob();
    console.log('PDF fetched successfully from S3, size:', blob.size, 'bytes');
    return blob;
  } catch (error) {
    console.error('Failed to fetch PDF from S3:', error);
    throw error;
  }
};

/**
 * Fetch annotation image from S3 as base64
 * @param {string} contractNo - Contract number
 * @returns {Promise<string|null>} - Base64 image data or null if not found
 */
export const getAnnotationImageFromS3 = async (vehicleId) => {
  try {
    console.log('Fetching annotation image from S3 for vehicleId:', vehicleId);
    
    // Get presigned URL for reading
    const presignedResult = await getS3PresignedUrlForRead(vehicleId, 'image');
    
    if (!presignedResult || !presignedResult.url) {
      console.log('No presigned URL returned for annotation image');
      return null;
    }
    
    console.log('Fetching image from S3 URL...');
    
    // Fetch the image
    const response = await fetch(presignedResult.url, {
      method: 'GET',
    });
    
    if (!response.ok) {
      console.log('Image not found in S3, status:', response.status);
      return null;
    }
    
    // Convert to blob then to base64
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('Annotation image fetched successfully from S3');
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to fetch annotation image from S3:', error);
    return null; // Return null instead of throwing - image is optional
  }
};

/**
 * Upload file to S3 using presigned URL
 * @param {string} presignedUrl - The presigned URL from getS3PresignedUrl
 * @param {File|Blob} file - The file to upload
 * @returns {boolean} - True if upload successful
 */
export const uploadToS3 = async (presignedUrl, file) => {
  try {
    // Use fetch instead of axios to avoid CORS preflight issues
    // The presigned URL already includes all necessary authentication
    // Don't send Content-Type header as it's not included in the signed headers
    // This avoids SignatureDoesNotMatch errors
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('S3 upload error response:', errorText);
      throw new Error(`S3 upload failed with status: ${response.status}`);
    }
    
    console.log('File uploaded to S3 successfully');
    return true;
  } catch (error) {
    console.error('Failed to upload file to S3:', error);
    throw new Error('Failed to upload file to S3');
  }
};

/**
 * Upload annotation image to S3
 * @param {string} contractNo - Contract number
 * @param {File|Blob} imageFile - The annotation image file
 * @returns {Object} - Upload result with filename and S3 key
 */
export const uploadAnnotationToS3 = async (vehicleId, imageFile) => {
  try {
    // Step 1: Get presigned URL for image
    const presignedResult = await getS3PresignedUrl(vehicleId, 'image');
    
    // Step 2: Upload to S3
    await uploadToS3(presignedResult.url, imageFile);
    
    const filename = generateAnnotationFilename(vehicleId);
    console.log('Annotation image uploaded successfully:', filename);
    
    return {
      success: true,
      filename: filename,
      key: presignedResult.key,
      bucketName: presignedResult.bucketName
    };
  } catch (error) {
    console.error('Failed to upload annotation to S3:', error);
    throw error;
  }
};

/**
 * Upload PDF to S3
 * @param {string} contractNo - Contract number
 * @param {Blob} pdfBlob - The PDF blob
 * @returns {Object} - Upload result with filename and S3 key
 */
export const uploadPdfToS3 = async (contractNo, pdfBlob) => {
  try {
    // Step 1: Get presigned URL for PDF
    const presignedResult = await getS3PresignedUrl(contractNo, 'pdf');
    
    // Step 2: Upload to S3 without Content-Type header (not in signed headers)
    const response = await fetch(presignedResult.url, {
      method: 'PUT',
      body: pdfBlob,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('S3 PDF upload error response:', errorText);
      throw new Error(`S3 PDF upload failed with status: ${response.status}`);
    }
    
    const filename = generatePdfFilename(contractNo);
    console.log('PDF uploaded to S3 successfully:', filename);
    
    return {
      success: true,
      filename: filename,
      key: presignedResult.key,
      bucketName: presignedResult.bucketName
    };
  } catch (error) {
    console.error('Failed to upload PDF to S3:', error);
    throw new Error('Failed to upload PDF to S3');
  }
};

// Update motorApiService to include S3 functions
motorApiService.getS3PresignedUrl = getS3PresignedUrl;
motorApiService.uploadToS3 = uploadToS3;
motorApiService.uploadAnnotationToS3 = uploadAnnotationToS3;

// UCS API configuration
const UCS_API_DOMAIN = process.env.REACT_APP_UCS_API_DOMAIN || 'https://os-uat.dch-holdings.com';
const UCS_API_USERNAME = process.env.REACT_APP_UCS_API_USERNAME;
const UCS_API_PASSWORD = process.env.REACT_APP_UCS_API_PASSWORD;

/**
 * Upload eScoreCard PDF document to UCS
 * @param {Object} data - { eScoreCardNo, filename, pdfBlob }
 * @returns {Object} - Response
 */
export const uploadDocumentToUCS = async (data) => {
  try {
    // Convert PDF blob to base64
    const arrayBuffer = await data.pdfBlob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64Content = btoa(binary);

    const response = await axios.post(
      `${UCS_API_DOMAIN}/UCS_MultipartFormData/rest/eScoreCard/UploadDocument`,
      {
        eScoreCardNo: data.eScoreCardNo,
        Document: {
          Filename: data.filename,
          FileContent: base64Content,
          Description: data.filename
        }
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        auth: {
          username: UCS_API_USERNAME,
          password: UCS_API_PASSWORD
        }
      }
    );

    console.log('PDF uploaded to UCS:', response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to upload PDF to UCS:', error);
    throw new Error('Failed to upload PDF to UCS');
  }
};


/**
 * Generate PDF filename based on contract number
 * @param {string} contractNo - Contract number (e.g., 'UCC/P00004/2025')
 * @returns {string} - Formatted filename (e.g., 'eScoreCard_P00004_2025.pdf')
 */
export const generatePdfFilename = (contractNo) => {
  // Convert 'UCC/P00004/2025' to 'P00004_2025'
  const sanitized = contractNo.replace(/UCC\/P/g, 'P').replace(/\//g, '_');
  return `eScoreCard_${sanitized}.pdf`;
};

/**
 * Create eScoreCard record in UCS database
 * @param {Object} data - { ContractNo, ExteriorScore, InteriorGrade }
 * @returns {Object} - Response with Code and Message (eScoreCardNumber)
 */
export const createEScoreCardInUCS = async (data) => {
  try {
    const response = await axios.post(
      `${UCS_API_DOMAIN}/UCS_MultipartFormData/rest/eScoreCard/CreateUpdate`,
      {
        ContractNo: data.ContractNo,
        ExteriorScore: data.ExteriorScore,
        InteriorGrade: data.InteriorGrade
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        auth: {
          username: UCS_API_USERNAME,
          password: UCS_API_PASSWORD
        }
      }
    );
    
    console.log('eScoreCard created in UCS:', response.data);
    
    if (response.data.Code === 200) {
      return {
        success: true,
        eScoreCardNumber: response.data.Message
      };
    } else {
      throw new Error(response.data.Message || 'Failed to create eScoreCard');
    }
  } catch (error) {
    console.error('Failed to create eScoreCard in UCS:', error);
    throw new Error('Failed to create eScoreCard record');
  }
};

/**
 * Format date to SQL datetime format
 * @param {Date|string} date - Date to format
 * @returns {string} - Formatted date string (e.g., '2026-03-10 11:10:59.442')
 */
const formatDateTimeForSQL = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

/**
 * Create Inspection record in MM database
 * @param {Object} data - Inspection data
 * @returns {Object} - Response with InspectionId
 */
export const createInspectionInMM = async (data) => {
  try {
    const token = await getMotorApiToken();
    const currentDateTime = formatDateTimeForSQL(new Date());
    
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/post-api`,
      {
        token: token,
        app_id: 45,
        data: [{
          target_api_code: "esc-2",
          ContractNo: data.ContractNo,
          Make: data.Make,
          Model: data.Model,
          CarInDate: data.CarInDate ? formatDateTimeForSQL(data.CarInDate) : null,
          LicenseNo: data.LicenseNo,
          CylinderCapacity: String(data.CylinderCapacity || ''),
          ManufacturingYear: data.ManufacturingYear,
          Mileage: String(data.Mileage || ''),
          NoOfPreviousOwners: data.NoOfPreviousOwners,
          ExteriorScore: data.ExteriorScore,
          InteriorGrade: data.InteriorGrade,
          AnnotatedImagePath: generateAnnotationFilename(data.VehicleID),
          PdfPath: generatePdfFilename(data.ContractNo),
          Inspector: data.Inspector,
          InspectorId: data.InspectorId || null,
          InspectorEmail: data.InspectorEmail,
          CreatedAt: currentDateTime,
          CreatedBy: data.Inspector,
          UpdatedAt: currentDateTime,
          UpdatedBy: data.Inspector
        }]
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    console.log('Inspection created in MM:', response.data);
    
    const result = response.data?.result?.[0]?.[0];
    if (result?.Message === 'success') {
      return {
        success: true,
        inspectionId: result.Data.Id
      };
    } else {
      throw new Error(result?.Message || 'Failed to create inspection');
    }
  } catch (error) {
    console.error('Failed to create inspection in MM:', error);
    throw new Error('Failed to create inspection record');
  }
};

/**
 * Create InspectionDetails records in MM database
 * @param {Object} data - { ContractNo, InspectionId, details: Array }
 * @returns {Object} - Response
 */
export const createInspectionDetailsInMM = async (data) => {
  try {
    const token = await getMotorApiToken();
    
    // Build data array for each detail item
    const detailsData = data.details.map(detail => ({
      target_api_code: "esc-3",
      ContractNo: data.ContractNo,
      CategoryId: detail.categoryId,
      CategoryName: detail.category,
      FormItemId: detail.formItemId,
      DamageCode: detail.damageCode,
      Description: detail.description,
      Weight: detail.weight,
      Count: detail.count,
      ScoreDeduction: detail.scoreDeduction,
      InspectionId: parseInt(data.InspectionId)
    }));
    
    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/post-api`,
      {
        token: token,
        app_id: 45,
        data: detailsData
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    console.log('InspectionDetails created in MM:', response.data);
    
    // Check if all details were created successfully
    const results = response.data?.result?.[0] || [];
    const allSuccess = results.every(r => r?.Message === 'success');
    
    if (allSuccess || results.length === 0) {
      return { success: true };
    } else {
      const failedItems = results.filter(r => r?.Message !== 'success');
      console.error('Some inspection details failed:', failedItems);
      throw new Error('Some inspection details failed to save');
    }
  } catch (error) {
    console.error('Failed to create inspection details in MM:', error);
    throw new Error('Failed to create inspection details');
  }
};

/**
 * Submit complete inspection (orchestrates PDF generation, upload, and all API calls)
 * @param {Object} inspectionData - Complete inspection data
 * @param {Object} user - Current user info
 * @returns {Object} - Result with eScoreCardNumber
 */
export const submitInspection = async (inspectionData, user) => {
  try {
    // Import PDF service dynamically to avoid circular dependency
    const { generateEScoreCardPDF } = await import('./pdfService');
    
    // Step 1: Fetch annotation image from S3 (if exists)
    console.log('Step 1: Fetching annotation image from S3...');
    let annotationImageBase64 = null;
    try {
      annotationImageBase64 = await getAnnotationImageFromS3(inspectionData.vehicleId);
      if (annotationImageBase64) {
        console.log('Annotation image fetched successfully');
      } else {
        console.log('No annotation image found in S3, proceeding without it');
      }
    } catch (error) {
      console.log('Could not fetch annotation image, proceeding without it:', error.message);
    }
    
    // Step 2: Generate PDF
    console.log('Step 2: Generating eScoreCard PDF...');
    const pdfData = {
      contractNo: inspectionData.contractNo,
      make: inspectionData.make,
      model: inspectionData.model,
      carInDate: inspectionData.carInDate,
      licenseNo: inspectionData.licenseNo,
      cylinderCapacity: inspectionData.cylinderCapacity,
      year: inspectionData.year,
      mileage: inspectionData.mileage,
      numberOfOwners: inspectionData.numberOfOwners,
      exteriorScore: inspectionData.exteriorScore,
      interiorGrade: inspectionData.interiorGrade,
      details: inspectionData.details,
      inspector: user?.name || user?.email || 'Unknown'
    };
    
    const pdfBlob = await generateEScoreCardPDF(pdfData, annotationImageBase64);
    console.log('PDF generated, size:', pdfBlob.size, 'bytes');
    
    // Step 3: Upload PDF to S3
    console.log('Step 3: Uploading PDF to S3...');
    const pdfUploadResult = await uploadPdfToS3(inspectionData.contractNo, pdfBlob);
    console.log('PDF uploaded to S3:', pdfUploadResult.filename);
    
    // Step 4: Create eScoreCard in UCS
    console.log('Step 4: Creating eScoreCard in UCS...');
    const ucsResult = await createEScoreCardInUCS({
      ContractNo: inspectionData.contractNo,
      ExteriorScore: inspectionData.exteriorScore,
      InteriorGrade: inspectionData.interiorGrade
    });
    console.log('eScoreCard created:', ucsResult.eScoreCardNumber);

    // Step 4b: Upload PDF to UCS
    console.log('Step 4b: Uploading PDF to UCS...');
    await uploadDocumentToUCS({
      eScoreCardNo: ucsResult.eScoreCardNumber,
      filename: pdfUploadResult.filename,
      pdfBlob: pdfBlob
    });
    console.log('PDF uploaded to UCS');
    
    // Step 5: Create Inspection in MM
    console.log('Step 5: Creating Inspection in MM...');
    const mmResult = await createInspectionInMM({
      ContractNo: inspectionData.contractNo,
      VehicleID: inspectionData.vehicleId,
      Make: inspectionData.make,
      Model: inspectionData.model,
      CarInDate: inspectionData.carInDate,
      LicenseNo: inspectionData.licenseNo,
      CylinderCapacity: inspectionData.cylinderCapacity,
      ManufacturingYear: inspectionData.year,
      Mileage: inspectionData.mileage,
      NoOfPreviousOwners: inspectionData.numberOfOwners,
      ExteriorScore: inspectionData.exteriorScore,
      InteriorGrade: inspectionData.interiorGrade,
      Inspector: user?.name || user?.email || null,
      InspectorId: null,
      InspectorEmail: user?.email || null
    });
    console.log('Inspection created with ID:', mmResult.inspectionId);
    
    // Step 6: Create InspectionDetails in MM (only save items with count > 0)
    const detailsWithCount = Object.values(inspectionData.details).filter(d => d.count > 0);
    
    if (detailsWithCount.length > 0) {
      console.log('Step 6: Creating InspectionDetails in MM...', detailsWithCount.length, 'items');
      await createInspectionDetailsInMM({
        ContractNo: inspectionData.contractNo,
        InspectionId: mmResult.inspectionId,
        details: detailsWithCount
      });
      console.log('InspectionDetails created successfully');
    } else {
      console.log('Step 6: No inspection details to create');
    }
    
    return {
      success: true,
      eScoreCardNumber: ucsResult.eScoreCardNumber,
      inspectionId: mmResult.inspectionId,
      pdfFilename: pdfUploadResult.filename
    };
  } catch (error) {
    console.error('Submit inspection failed:', error);
    throw error;
  }
};

/**
 * Get submitted inspection details from MM database
 * @param {string} contractNo - Contract number to search
 * @returns {Object} - Inspection details data
 */
export const getInspectionDetailsFromMM = async (contractNo) => {
  try {
    const token = await getMotorApiToken();

    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/post-api`,
      {
        token: token,
        app_id: 45,
        data: [{
          target_api_code: "esc-6",
          page: 1,
          page_size: 0,
          sort_column: "FormItemId",
          sort_order: "asc",
          search_text: contractNo
        }]
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const resultData = response.data?.result?.[0]?.[0]?.Data || [];
    console.log('Inspection details fetched from MM:', resultData.length, 'items');
    return resultData;
  } catch (error) {
    console.error('Failed to get inspection details from MM:', error);
    throw new Error('Failed to load inspection details');
  }
};

// Update motorApiService to include new functions
motorApiService.generatePdfFilename = generatePdfFilename;
motorApiService.generateAnnotationFilename = generateAnnotationFilename;
motorApiService.createEScoreCardInUCS = createEScoreCardInUCS;
motorApiService.uploadDocumentToUCS = uploadDocumentToUCS;
motorApiService.createInspectionInMM = createInspectionInMM;
motorApiService.createInspectionDetailsInMM = createInspectionDetailsInMM;
motorApiService.submitInspection = submitInspection;
motorApiService.getS3PresignedUrlForRead = getS3PresignedUrlForRead;
motorApiService.getAnnotationImageFromS3 = getAnnotationImageFromS3;
motorApiService.uploadPdfToS3 = uploadPdfToS3;
motorApiService.getPdfFromS3 = getPdfFromS3;
motorApiService.getInspectionDetailsFromMM = getInspectionDetailsFromMM;

/**
 * Update an existing InspectionRecord in MM database
 * @param {Object} data - Inspection record data
 * @returns {Object} - Response
 */
export const updateInspectionInMM = async (data) => {
  try {
    const token = await getMotorApiToken();

    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/post-api`,
      {
        token: token,
        app_id: 45,
        data: [{
          target_api_code: "esc-4",
          Id: data.Id,
          ContractNo: data.ContractNo,
          Make: data.Make,
          Model: data.Model,
          CarInDate: data.CarInDate,
          LicenseNo: data.LicenseNo,
          CylinderCapacity: String(data.CylinderCapacity),
          ManufacturingYear: data.ManufacturingYear,
          Mileage: String(data.Mileage),
          NoOfPreviousOwners: data.NoOfPreviousOwners,
          ExteriorScore: data.ExteriorScore,
          InteriorGrade: data.InteriorGrade,
          AnnotatedImagePath: data.AnnotatedImagePath,
          PdfPath: data.PdfPath,
          Inspector: data.Inspector,
          InspectorId: data.InspectorId,
          InspectorEmail: data.InspectorEmail,
          UpdatedAt: data.UpdatedAt,
          UpdatedBy: data.UpdatedBy
        }]
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    console.log('Inspection record updated in MM:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to update inspection in MM:', error);
    throw new Error('Failed to update inspection record');
  }
};

/**
 * Remove all InspectionDetails for a contract in MM database
 * @param {string} contractNo - Contract number
 * @returns {Object} - Response
 */
export const removeInspectionDetailsInMM = async (contractNo) => {
  try {
    const token = await getMotorApiToken();

    const response = await axios.post(
      `${MOTOR_API_DOMAIN}/cmm-api/post-api`,
      {
        token: token,
        app_id: 45,
        data: [{
          target_api_code: "esc-7",
          ContractNo: contractNo
        }]
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    console.log('Inspection details removed in MM:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to remove inspection details in MM:', error);
    throw new Error('Failed to remove inspection details');
  }
};

motorApiService.updateInspectionInMM = updateInspectionInMM;
motorApiService.removeInspectionDetailsInMM = removeInspectionDetailsInMM;
